import React, { useState } from "react";
import axios from "axios";
import { AppleLogo, PlayLogo } from "./Logo";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Info, DownloadSimple, ShieldCheck, DeviceMobile, Rocket } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const track = (platform) => {
  axios.post(`${API}/downloads/track`, { platform }).catch(() => {});
};

const PlantusLeaf = ({ className = "", fill = "#1F4D36" }) => (
  <svg viewBox="0 0 40 50" fill="none" aria-hidden className={className}>
    <path
      d="M 9 46 L 9 12"
      stroke={fill}
      strokeWidth="2.8"
      strokeLinecap="round"
    />
    <path
      d="M 9 12 C 11 4, 22 2, 28 6 C 33 9, 33 17, 27 22 C 21 27, 11 26, 9 24"
      stroke={fill}
      strokeWidth="2.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M 12 19 Q 19 14 26 8"
      stroke={fill}
      strokeWidth="1.2"
      strokeLinecap="round"
      fill="none"
      opacity="0.7"
    />
    <path
      d="M 22 4 Q 26 0 30 2"
      stroke={fill}
      strokeWidth="1.6"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

export const StoreButton = ({ platform, testId, variant = "dark" }) => {
  const cfg = {
    ios: {
      label: "Laden im",
      brand: "App Store",
      Icon: AppleLogo,
      onClick: () => track("ios"),
      href: "https://apps.apple.com/",
    },
    android: {
      label: "Jetzt bei",
      brand: "Google Play",
      Icon: PlayLogo,
      onClick: () => track("android"),
      href: "https://play.google.com/",
    },
  }[platform];

  const isDark = variant === "dark";
  return (
    <a
      href={cfg.href}
      target="_blank"
      rel="noreferrer"
      onClick={cfg.onClick}
      data-testid={testId}
      className={`group inline-flex items-center gap-3 rounded-2xl px-5 py-3 transition-all shadow-sm hover:shadow-lg hover:-translate-y-0.5 ${
        isDark
          ? "bg-[#1A1D1A] text-white hover:bg-black"
          : "bg-white text-[#1A1D1A] hover:bg-bone-100 border border-bone-200"
      }`}
    >
      <cfg.Icon className="w-7 h-7" />
      <span className="flex flex-col leading-tight">
        <span className="text-[10px] uppercase tracking-widest opacity-70">
          {cfg.label}
        </span>
        <span className="text-base font-semibold font-heading">{cfg.brand}</span>
      </span>
    </a>
  );
};

export const ApkButton = ({ testId, variant = "light" }) => {
  const isDark = variant === "dark";
  const [showInfo, setShowInfo] = useState(false);
  return (
    <div className="flex flex-col gap-2 items-start">
      <a
        href={`${API}/downloads/apk`}
        onClick={() => track("apk")}
        data-testid={testId}
        className={`group inline-flex items-center gap-3 rounded-2xl px-5 py-3 transition-all shadow-sm hover:shadow-lg hover:-translate-y-0.5 ${
          isDark
            ? "bg-forest-700/40 text-white border border-white/20 hover:bg-forest-700/60"
            : "bg-forest-50 text-forest-700 border border-forest-100 hover:bg-forest-100"
        }`}
      >
        <PlantusLeaf
          className="w-7 h-7 flex-shrink-0"
          fill={isDark ? "#F9F8F6" : "#1F4D36"}
        />
        <span className="flex flex-col leading-tight text-left">
          <span
            className={`text-[10px] uppercase tracking-widest ${
              isDark ? "opacity-80" : "opacity-70"
            }`}
          >
            Direkt-Download
          </span>
          <span className="text-base font-semibold font-heading">
            Plantus APK
          </span>
        </span>
      </a>

      <button
        type="button"
        onClick={() => setShowInfo(true)}
        data-testid={`${testId}-help`}
        className={`ml-1 inline-flex items-center gap-1.5 font-body text-[12px] hover:underline transition-colors ${
          isDark ? "text-white/70 hover:text-white" : "text-[#4A524C] hover:text-forest-700"
        }`}
      >
        <Info size={13} weight="regular" />
        So installierst du die APK
      </button>

      <ApkInstallDialog open={showInfo} onOpenChange={setShowInfo} />
    </div>
  );
};


const InstallStep = ({ number, icon: Icon, title, text }) => (
  <div className="flex gap-3 items-start">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-forest-50 border border-forest-100 flex items-center justify-center">
      <span className="font-heading font-semibold text-forest-700 text-sm">{number}</span>
    </div>
    <div className="flex-1 pt-0.5">
      <div className="font-heading font-medium text-forest-900 text-[15px] flex items-center gap-2">
        <Icon size={16} weight="duotone" className="text-forest-600" />
        {title}
      </div>
      <p className="font-body text-sm text-[#4A524C] mt-0.5 leading-relaxed">{text}</p>
    </div>
  </div>
);


const ApkInstallDialog = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      className="max-w-lg bg-white border-bone-200"
      data-testid="apk-install-dialog"
    >
      <DialogHeader>
        <DialogTitle className="font-heading text-2xl text-forest-900 flex items-center gap-2">
          <DeviceMobile size={24} weight="duotone" className="text-forest-600" />
          APK auf Android installieren
        </DialogTitle>
        <DialogDescription className="font-body text-[15px] text-[#4A524C]">
          In 4 Schritten einsatzbereit &ndash; keine 2 Minuten.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 mt-2">
        <InstallStep
          number="1"
          icon={DownloadSimple}
          title="APK herunterladen"
          text={'Tippe oben auf „Plantus APK". Die Datei landet in deinem Download-Ordner.'}
        />
        <InstallStep
          number="2"
          icon={ShieldCheck}
          title="Installation zulassen"
          text="Beim Öffnen fragt Android einmalig, ob du Apps aus dieser Quelle installieren möchtest. Häkchen setzen und bestätigen."
        />
        <InstallStep
          number="3"
          icon={Rocket}
          title="Plantus installiert sich"
          text="Ca. 30 Sekunden, dann findest du das Plantus-Icon im App-Menü."
        />
        <InstallStep
          number="4"
          icon={DeviceMobile}
          title="Konto verbinden"
          text="Öffne die App, melde dich mit deinem Plantus-Konto an, deine Flächen und Anwendungen synchronisieren sich automatisch mit plantus-app.com."
        />
      </div>

      <div
        className="mt-4 rounded-xl bg-forest-50 border border-forest-100 p-3.5 flex gap-2.5 items-start"
        data-testid="apk-install-info-note"
      >
        <ShieldCheck size={18} weight="duotone" className="text-forest-700 flex-shrink-0 mt-0.5" />
        <div className="font-body text-[13px] text-forest-800 leading-relaxed">
          <strong className="text-forest-900">Ist die APK sicher?</strong>
          <br />
          Ja &ndash; die Datei wird direkt von unseren Servern ausgeliefert. Sie ist identisch mit der Version, die wir bei Google Play einreichen. Prüfsumme (SHA-256) auf Anfrage verfügbar.
        </div>
      </div>
    </DialogContent>
  </Dialog>
);
